/** Tag → Used by tab.
 *
 *  Where this tag is actually applied. The tag detail payload carries no usage
 *  aggregates, so this is deliberately a list of pre-built searches rather than
 *  counts — a number nobody can act on would need a round trip per grid, and
 *  the searches are what an operator wants anyway ("which bookings carry this?").
 *
 *  Every link is built by `tagLinks`, which owns the id spaces: the tag pins
 *  with `=#<id>` on the booking / order / tour grids, the users grid takes the
 *  raw id as a plain param, and the feature-flags grid can only match the chip
 *  TEXT — that last one over-matches a same-named tag and says so.
 */
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { TagDetail } from '@/api/tags';

import { tagUsedByLinks } from '../tagLinks';

export function TagUsedByTab({ tag }: { tag: TagDetail }) {
  const links = tagUsedByLinks(tag, { tenantId: tag.tenant?.id ?? null });
  return (
    <Stack spacing={1.5} sx={{ maxWidth: 640 }}>
      <Typography variant="body2" color="text.secondary">
        Searches for this tag across the console. Each opens the grid pre-filtered — counts are not
        part of the tag record, so these are links, not totals.
      </Typography>
      {links.map((l) => (
        <Box key={l.key}>
          <MuiLink component={RouterLink} to={l.href} underline="hover" sx={{ fontSize: 14 }}>
            {l.label} →
          </MuiLink>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ fontFamily: 'monospace' }}
          >
            {l.hint}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}
